import React, { Component } from 'react';
import { ArrowLeftIcon, PaperClipIcon, XMarkIcon, PlusIcon } from '@heroicons/react/24/outline';
import { Dialog, Transition } from '@headlessui/react';
import { toast } from 'react-toastify';
import apiService from '../../services/apiService';
import LoadingSpinner from '../common/LoadingSpinner';

class AddCommentForm extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      comments: [],
      totalCount: 0,
      validated: false,
      saving: false,
      commentContent: '',
      refresh: false,
      attachments: [],
      isDragOver: false,
      currentPage: 1,
      hasMoreComments: true,
      loadingMore: false,
      showAddNoteModal: false,
    };
    this.fileInputRef = React.createRef();
    this.commentsEndRef = React.createRef();
  }

  componentDidMount() {
    this.loadComments();
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.refresh !== this.state.refresh) {
      this.loadComments();
    }
  }

  loadComments = async () => {
    const { crmid, onLogout } = this.props;
    
    this.setState({ loading: true });
    
    try {
      
      // Load first page of comments
      const result = await apiService.getRelatedComments(crmid, 1);
      
      
      if (result.success) {
        const responseData = result.data;
        const comments = responseData.comments || [];
        const totalCount = responseData.total_count || 0;
        const hasMore = comments.length < totalCount;
        
        
        this.setState({
          comments: comments,
          currentPage: 1,
          hasMoreComments: hasMore,
          totalCount: totalCount,
          loading: false,
        });
      } else if (result.needsLogout && onLogout) {
        onLogout();
      } else {
        this.setState({ loading: false });
      }
    } catch (error) {
      this.setState({ loading: false });
    }
  };

  loadMoreComments = async () => {
    const { crmid, onLogout } = this.props;
    const { currentPage, loadingMore, hasMoreComments } = this.state;
    
    if (loadingMore || !hasMoreComments) return;
    
    this.setState({ loadingMore: true });
    
    try {
      const nextPage = currentPage + 1;
      
      const result = await apiService.getRelatedComments(crmid, nextPage);
      
      if (result.success) {
        const responseData = result.data;
        const newComments = responseData.comments || [];
        const totalCount = responseData.total_count || 0;
        
        
        this.setState(prevState => ({
          comments: [...prevState.comments, ...newComments],
          currentPage: nextPage,
          hasMoreComments: prevState.comments.length + newComments.length < totalCount,
          totalCount: totalCount,
          loadingMore: false,
        }));
      } else if (result.needsLogout && onLogout) {
        onLogout();
      } else {
        this.setState({ loadingMore: false });
      }
    } catch (error) {
      this.setState({ loadingMore: false });
    }
  };

  handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const { hasMoreComments, loadingMore } = this.state;
    
    // Load more when scrolled to bottom (with 100px threshold)
    if (scrollHeight - scrollTop <= clientHeight + 100 && hasMoreComments && !loadingMore) {
      this.loadMoreComments();
    }
  };
  handleSubmit = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const form = event.currentTarget;
    if (form.checkValidity()) {
      this.setState({ validated: false, saving: true });

      const formData = new FormData(form);
      const formDataObj = Object.fromEntries(formData.entries());
      
      // Create the data structure for SaveRelatedComments operation
      const commentData = {
        operation: 'SaveRelatedComment',
        element: JSON.stringify({
          relatedto: formDataObj.crmid,
          comment: formDataObj.commentcontent,
          // Add any other related data here
        })
      };

      
      try {
        const result = await apiService.saveComment(commentData, this.state.attachments);
        
        
        if (result.success) {
          this.setState({
            commentContent: '',
            attachments: [],
            currentPage: 1,
            totalCount: 0,
            refresh: !this.state.refresh,
            showAddNoteModal: false,
          });
          if (this.fileInputRef.current) {
            this.fileInputRef.current.value = '';
          }
          toast.success('Note saved successfully', {
            closeOnClick: true,
            pauseOnHover: true,
            theme: 'colored',
            autoClose: 3000,
          });
        } else if (result.needsLogout && this.props.onLogout) {
          this.props.onLogout();
        } else {
          toast.error(result.error || 'Failed to save note', {
            closeOnClick: true,
            pauseOnHover: true,
            theme: 'colored',
            autoClose: 5000,
          });
        }
      } catch (error) {
        toast.error('An error occurred while saving note', {
          closeOnClick: true,
          pauseOnHover: true,
          theme: 'colored',
          autoClose: 5000,
        });
      } finally {
        this.setState({ saving: false });
      }
    } else {
      this.setState({ validated: true });
    }
  };

  handleCommentChange = (e) => {
    this.setState({ commentContent: e.target.value });
  };

  handleReset = () => {
    this.setState({ commentContent: '' });
  };

  handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ isDragOver: true });
  };

  handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ isDragOver: false });
  };

  handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ isDragOver: false });
    
    const files = Array.from(e.dataTransfer.files);
    this.processFiles(files);
  };

  handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    this.processFiles(files);
  };

  processFiles = (files) => {
    const validFiles = [];
    const maxSize = 10 * 1024 * 1024; // 10MB
    
    files.forEach(file => {
      if (file.size > maxSize) {
        toast.error(`File "${file.name}" is too large. Maximum size is 10MB.`, {
          closeOnClick: true,
          pauseOnHover: true,
          theme: 'colored',
          autoClose: 5000,
        });
      } else {
        validFiles.push(file);
      }
    });
    
    if (validFiles.length > 0) {
      this.setState(prevState => ({
        attachments: [...prevState.attachments, ...validFiles]
      }));
    }
  };

  removeAttachment = (index) => {
    this.setState(prevState => ({
      attachments: prevState.attachments.filter((_, i) => i !== index)
    }));
  };

  render() {
    const { 
      loading, 
      comments, 
      totalCount,
      validated, 
      saving, 
      commentContent, 
      attachments, 
      isDragOver,
      hasMoreComments,
      loadingMore,
      showAddNoteModal
    } = this.state;
    const { crmid, onCancel } = this.props;

    return (
      <div className="bg-white min-h-screen">
        {/* Add Note Modal */}
        <Transition appear show={showAddNoteModal} as={React.Fragment}>
          <Dialog as="div" className="relative z-50" onClose={() => this.setState({ showAddNoteModal: false })}>
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black bg-opacity-25" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4 text-center">
                <Transition.Child
                  as={React.Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-lg bg-white text-left align-middle shadow-xl transition-all">
                    {/* Modal Header */}
                    <div className="flex items-center justify-between p-6 border-b border-gray-200">
                      <Dialog.Title as="h3" className="text-lg font-semibold text-gray-900">
                        Add New Note
                      </Dialog.Title>
                      <button
                        onClick={() => this.setState({ showAddNoteModal: false })}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    </div>

                    {/* Modal Content */}
                    <div className="p-6">
                      <form
                        className={`space-y-4 ${validated ? 'was-validated' : ''}`}
                        onSubmit={this.handleSubmit}
                      >
                        <input type="hidden" name="crmid" value={crmid} />
                        
                        {/* Comment Content */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Note Content
                          </label>
                          <div className="relative">
                            <textarea
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent resize-none"
                              rows="4"
                              placeholder="Enter your note here..."
                              name="commentcontent"
                              required
                              value={commentContent}
                              onChange={this.handleCommentChange}
                            />
                          </div>
                        </div>
                        
                        {/* File Attachment Section */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Attachments
                            <span className="text-gray-500 font-normal text-xs ml-1">(Optional)</span>
                          </label>
                          
                          {/* File Drop Zone */}
                          <div
                            className={`relative border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
                              isDragOver 
                                ? 'border-primary-400 bg-primary-50' 
                                : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
                            }`}
                            onDragOver={this.handleDragOver}
                            onDragLeave={this.handleDragLeave}
                            onDrop={this.handleDrop}
                          >
                            <div className="space-y-2">
                              <div className={`w-8 h-8 mx-auto rounded-full flex items-center justify-center ${
                                isDragOver ? 'bg-primary-100' : 'bg-gray-100'
                              }`}>
                                <PaperClipIcon className={`w-4 h-4 ${
                                  isDragOver ? 'text-primary-600' : 'text-gray-400'
                                }`} />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-700">
                                  {isDragOver ? 'Drop files here' : 'Drag and drop files here'}
                                </p>
                                <p className="text-xs text-gray-500">
                                  or{' '}
                                  <button
                                    type="button"
                                    className="text-primary-600 hover:text-primary-700 underline"
                                    onClick={() => this.fileInputRef.current?.click()}
                                  >
                                    browse
                                  </button>
                                </p>
                              </div>
                            </div>
                            <input
                              ref={this.fileInputRef}
                              type="file"
                              multiple
                              className="hidden"
                              onChange={this.handleFileSelect}
                              accept="*/*"
                            />
                          </div>
                          
                          {/* Selected Files List */}
                          {attachments.length > 0 && (
                            <div className="mt-3 space-y-2">
                              <div className="flex items-center justify-between">
                                <p className="text-sm font-medium text-gray-700">
                                  Selected Files ({attachments.length})
                                </p>
                                <button
                                  type="button"
                                  onClick={() => this.setState({ attachments: [] })}
                                  className="text-xs text-gray-500 hover:text-red-600 transition-colors"
                                >
                                  Clear all
                                </button>
                              </div>
                              <div className="space-y-2">
                                {attachments.map((file, index) => (
                                  <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded border">
                                    <div className="flex items-center flex-1 min-w-0">
                                      <div className="w-6 h-6 bg-primary-100 rounded flex items-center justify-center mr-2">
                                        <PaperClipIcon className="w-4 h-4 text-primary-600" />
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className="text-xs font-medium text-gray-900 truncate">
                                          {file.name}
                                        </p>
                                        <p className="text-xs text-gray-500">
                                          {(file.size / 1024).toFixed(1)} KB
                                        </p>
                                      </div>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() => this.removeAttachment(index)}
                                      className="p-1 text-gray-400 hover:text-red-500 rounded transition-colors"
                                    >
                                      <XMarkIcon className="w-3 h-3" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                        
                        {/* Modal Actions */}
                        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                          <button
                            type="button"
                            onClick={() => this.setState({ 
                              showAddNoteModal: false,
                              commentContent: '',
                              attachments: []
                            })}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                            disabled={saving}
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            className="px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors disabled:opacity-50 flex items-center"
                            disabled={saving}
                          >
                            {saving ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                Saving...
                              </>
                            ) : (
                              'Save Note'
                            )}
                          </button>
                        </div>
                      </form>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>

        {/* Header */}
        <div className="border-b border-gray-200 p-4 space-y-3">
          {/* Back Button */}
          <div>
            <button
              onClick={onCancel}
              className="flex items-center text-sm text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeftIcon className="w-4 h-4 mr-2" />
              Back
            </button>
          </div>
          
          {/* Title and Add Button */}
          <div className="flex items-center justify-between">
            <h1 className="text-lg font-semibold text-gray-900">Notes</h1>
            <button
              onClick={() => this.setState({ showAddNoteModal: true })}
              className="flex items-center px-3 py-1.5 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors"
            >
              <PlusIcon className="w-4 h-4 mr-1" />
              Add Note
            </button>
          </div>
        </div>

        <div className="space-y-4">

          {/* Comments History */}
          <div className="bg-white">
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-900">Notes History</h3>
                <span className="text-sm text-gray-500">
                  {comments.length}{totalCount > comments.length ? ` of ${totalCount}` : ''}
                </span>
              </div>
            </div>
            
            <div 
              className="overflow-y-auto"
              style={{ height: 'auto', minHeight: '200px' }}
              onScroll={this.handleScroll}
            >
              {loading ? (
                <div className="flex justify-center py-8">
                  <LoadingSpinner label="Loading comments..." size="medium" />
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {Array.isArray(comments) && comments.map((comment, index) => (
                    <div key={index} className="px-4 py-3">
                      <div className="flex items-start space-x-3">
                        {/* Avatar */}
                        <div className="flex-shrink-0">
                          <div className="w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center">
                            <span className="text-white font-medium text-sm">
                              {(comment.commentedby || 'Unknown').charAt(0).toUpperCase()}
                            </span>
                          </div>
                        </div>
                        
                        {/* Comment Content */}
                        <div className="flex-1 min-w-0">
                          {/* Comment Text */}
                          <div className="mb-2">
                            <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
                              {comment.comment}
                            </p>
                          </div>
                          
                          {/* Attachments */}
                          {comment.files && comment.files.length > 0 && (
                            <div className="mb-2">
                              <div className="flex items-center mb-1">
                                <PaperClipIcon className="w-3 h-3 text-gray-400 mr-1" />
                                <span className="text-xs text-gray-500">
                                  {comment.files.length} file{comment.files.length > 1 ? 's' : ''}
                                </span>
                              </div>
                              <div className="space-y-1">
                                {comment.files.map((file, attIndex) => (
                                  <div key={attIndex} className="flex items-center text-xs text-gray-600">
                                    <span className="truncate">
                                      {file.name || file.filename || file}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {/* Comment Time */}
                          <div className="text-xs text-gray-400">
                            {comment.commentedon || 'Unknown Date'}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {(!Array.isArray(comments) || comments.length === 0) && (
                    <div className="text-center py-8 px-4">
                      <p className="text-gray-500 text-sm">No notes yet. Add the first note above.</p>
                    </div>
                  )}
                  
                  {/* Load More Indicator */}
                  {loadingMore && (
                    <div className="flex justify-center py-4 px-4">
                      <div className="flex items-center space-x-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
                        <span className="text-sm text-gray-500">Loading more notes...</span>
                      </div>
                    </div>
                  )}
                  
                  {/* End of Comments Indicator */}
                  {!hasMoreComments && Array.isArray(comments) && comments.length > 0 && (
                    <div className="text-center py-4 px-4 border-t border-gray-100">
                      <p className="text-xs text-gray-400">No more notes to load</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        </div>
  
    );
  }
}

export default AddCommentForm;